import React from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from "recharts";
import { KeyRound, Package, AlertTriangle, Coins, ArrowUpRight, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { displayProductName } from "@/lib/productName";
import OnboardingCard from "@/components/OnboardingCard";

function Stat({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="lp-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-lg bg-[#d4af37]/8 border border-[#d4af37]/25 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#d4af37]" />
        </div>
        {hint && <span className="text-[10px] uppercase tracking-widest text-white/40">{hint}</span>}
      </div>
      <div className="text-xs uppercase tracking-widest text-white/40 mt-4">{label}</div>
      <div className="font-display text-2xl text-white/95 mt-1">{value}</div>
    </div>
  );
}

export default function DashOverview() {
  const [data, setData] = React.useState(null);
  const { t } = useLang();

  React.useEffect(() => {
    api.get("/api/dashboard/overview").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 lp-shimmer rounded-2xl" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="dash-overview-page">
      <OnboardingCard hasApiKey={(data.api_keys_active || 0) > 0} hasUsage={(data.requests_30d || 0) > 0} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-white/95">{t("dash.overview.title")}</h1>
          <p className="text-white/50 text-sm mt-1">{t("dash.overview.subtitle")}</p>
        </div>
        <Link to="/marketplace" className="lp-btn-secondary" data-testid="overview-browse-apis">
          {t("dash.overview.browse")} <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Coins} label={t("dash.overview.stat.credits")} value={data.credits.toLocaleString()} hint={t("dash.overview.stat.credits_hint")} testid="stat-credits" />
        <Stat icon={Package} label={t("dash.overview.stat.subs")} value={data.subscriptions.length} testid="stat-subs" />
        <Stat icon={KeyRound} label={t("dash.overview.stat.keys")} value={data.api_keys_active} testid="stat-keys" />
        <Stat icon={TrendingUp} label={t("dash.overview.stat.requests")} value={data.requests_30d.toLocaleString()} hint={`${data.errors_30d} ${t("dash.overview.stat.errors")}`} testid="stat-requests" />
      </div>

      <div className="lp-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-white/90">{t("dash.overview.chart.title")}</h3>
          <span className="text-xs text-white/50 font-mono">{t("dash.overview.chart.axis")}</span>
        </div>
        <div className="h-64" data-testid="usage-chart">
          <ResponsiveContainer>
            <AreaChart data={data.daily_usage} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#d4af37" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#d4af37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: "#0a0b0e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }}
                labelStyle={{ color: "#d4af37" }}
                itemStyle={{ color: "white" }}
              />
              <Area type="monotone" dataKey="requests" stroke="#d4af37" strokeWidth={2} fill="url(#g1)" animationDuration={900} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="lp-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-white/90">{t("dash.overview.subs.title")}</h3>
            <Link to="/dashboard/subscriptions" className="text-[#d4af37] text-sm">{t("dash.overview.subs.manage")} →</Link>
          </div>
          <div className="space-y-3">
            {data.subscriptions.length === 0 && <div className="text-white/50 text-sm">{t("dash.overview.subs.empty")}</div>}
            {data.subscriptions.map((s) => {
              const pct = Math.round((s.monthly_requests_used / Math.max(1, s.monthly_requests_limit)) * 100);
              return (
                <div key={s.id} className="p-4 rounded-xl border border-white/8" data-testid={`sub-row-${s.product?.slug}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-white/90 font-medium">{displayProductName(s.product?.name)}</div>
                    <span className="lp-chip gold uppercase text-[10px]">{s.tier}</span>
                  </div>
                  <div className="text-xs text-white/50 mt-1">
                    {s.monthly_requests_used.toLocaleString()} / {s.monthly_requests_limit.toLocaleString()} · {pct}%
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/6 overflow-hidden">
                    <div className="h-full bg-[#d4af37]" style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lp-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-white/90">{t("dash.overview.errors.title")}</h3>
            <span className="lp-chip red"><AlertTriangle className="w-3 h-3" /> {data.errors_30d} {t("dash.overview.in_30d")}</span>
          </div>
          <div className="text-white/50 text-sm mb-4">
            {t("dash.overview.errors.body")}
          </div>
          <Link to="/dashboard/usage" className="lp-btn-secondary" data-testid="overview-view-usage">{t("dash.overview.errors.view")}</Link>
        </div>
      </div>
    </div>
  );
}
